import React, { useState } from "react";
import { motion } from "framer-motion";
import { LogIn, Lock, Phone, Building2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand";

const Stage = { LOGIN: "login", FORCE_CHANGE: "force_change", SELECT_STUDIO: "select_studio" };

const Login = ({ onLogin }) => {
  const { display_name } = useBrand();
  const [stage, setStage] = useState(Stage.LOGIN);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [studios, setStudios] = useState([]);

  const handleLogin = async () => {
    if (!phone || phone.length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (!password) {
      toast.error("Enter your password");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { phone, password });
      const { token, user, must_change_password, studios: assigned } = res.data;
      setPendingToken(token);
      setPendingUser(user);
      setStudios(assigned || []);
      // Persist token early so subsequent /auth/* calls are authenticated
      localStorage.setItem("token", token);

      if (must_change_password) {
        setStage(Stage.FORCE_CHANGE);
      } else {
        finalize(user, token, assigned);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForceChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: password,
        new_password: newPassword,
      });
      toast.success("Password changed");
      const updated = { ...pendingUser, must_change_password: false };
      setPendingUser(updated);
      finalize(updated, pendingToken, studios);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const finalize = (user, token, assigned) => {
    const role = user.role;
    // Students don't need a studio
    if (role === "student") {
      onLogin(user, token);
      return;
    }
    if (!assigned || assigned.length === 0) {
      // Super admin with no studios — proceed without scope
      localStorage.removeItem("active_studio_id");
      onLogin(user, token);
      return;
    }
    if (assigned.length === 1) {
      localStorage.setItem("active_studio_id", assigned[0].studio_id);
      localStorage.setItem("active_studio", JSON.stringify(assigned[0]));
      onLogin(user, token);
      return;
    }
    setStage(Stage.SELECT_STUDIO);
  };

  const selectStudio = (s) => {
    localStorage.setItem("active_studio_id", s.studio_id);
    localStorage.setItem("active_studio", JSON.stringify(s));
    onLogin(pendingUser, pendingToken);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="brand-gradient p-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-2" data-testid="login-title">
              {display_name}
            </h1>
            <p className="text-white/90 text-sm">Studio Management Made Simple</p>
          </div>

          {/* LOGIN STAGE */}
          {stage === Stage.LOGIN && (
            <div className="p-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="phone">Mobile Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    data-testid="login-phone-input"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="h-12 rounded-xl pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    className="h-12 rounded-xl pl-10"
                  />
                </div>
              </div>
              <Button
                data-testid="login-submit-button"
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-700 h-12 rounded-full font-medium text-base"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {loading ? "Signing in..." : "Sign In"}
              </Button>
              <div className="text-center text-xs text-slate-500 pt-2">
                Forgot password? Contact your studio admin to reset it.
              </div>
            </div>
          )}

          {/* FORCE CHANGE STAGE */}
          {stage === Stage.FORCE_CHANGE && (
            <div className="p-8 space-y-5">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Set a new password</p>
                  <p className="text-xs text-amber-800 mt-1">
                    For security, you must change your temporary password before continuing.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  data-testid="force-new-password-input"
                  type="password"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  data-testid="force-confirm-password-input"
                  type="password"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleForceChange()}
                  className="h-12 rounded-xl"
                />
              </div>
              <Button
                data-testid="force-change-submit-button"
                onClick={handleForceChange}
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-700 h-12 rounded-full font-medium text-base"
              >
                {loading ? "Updating..." : "Set New Password & Continue"}
              </Button>
            </div>
          )}

          {/* STUDIO SELECTION STAGE */}
          {stage === Stage.SELECT_STUDIO && (
            <div className="p-8 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Select Studio</h2>
                <p className="text-sm text-slate-500 mt-1">
                  You have access to multiple studios. Pick one to continue.
                </p>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {studios.map((s) => (
                  <button
                    key={s.studio_id}
                    data-testid={`studio-option-${s.studio_id}`}
                    onClick={() => selectStudio(s)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-violet-500 hover:bg-violet-50 transition text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500">Tap to enter</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
